import React, { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  PieChart,
  History,
  Bookmark,
  Grid,
  MoreHorizontal,
  Landmark,
  CreditCard,
  Settings,
  ChevronRight,
  Wallet,
} from 'lucide-react'
import { cn } from '../lib/utils'

interface Tab {
  path: string
  label: string
  icon: React.ElementType
}

const PRIMARY_TABS: Tab[] = [
  { path: '/', label: 'Portfolio', icon: PieChart },
  { path: '/trading', label: 'Trading', icon: History },
  { path: '/watchlist', label: 'Watchlist', icon: Bookmark },
  { path: '/heatmap', label: 'Market', icon: Grid },
]

const MORE_TABS: Tab[] = [
  { path: '/banking', label: 'Banking', icon: Landmark },
  { path: '/credit', label: 'Credit', icon: CreditCard },
  { path: '/settings', label: 'Settings', icon: Settings },
]

/** iOS-style bottom tab bar, shown below the `lg` breakpoint in place of the header nav. */
const MobileTabBar: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)

  const moreActive = MORE_TABS.some((t) => t.path === pathname)

  useEffect(() => setMoreOpen(false), [pathname])

  const go = (path: string) => {
    // Tapping the active tab scrolls to top, like UITabBarController
    if (path === pathname) window.scrollTo({ top: 0, behavior: 'smooth' })
    else navigate(path)
  }

  return (
    <>
      <nav
        className="tabbar lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-200/80 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl no-print"
        aria-label="Main navigation"
      >
        <div className="flex h-14 max-w-xl mx-auto">
          {PRIMARY_TABS.map(({ path, label, icon: Icon }) => {
            const active = pathname === path
            return (
              <button
                key={path}
                onClick={() => go(path)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'tabbar-item flex-1 flex flex-col items-center justify-center gap-0.5',
                  active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500',
                )}
              >
                <Icon className="w-6 h-6" strokeWidth={active ? 2.25 : 1.75} />
                <span className="text-[10px] font-medium leading-none">{label}</span>
              </button>
            )
          })}
          <button
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            className={cn(
              'tabbar-item flex-1 flex flex-col items-center justify-center gap-0.5',
              moreActive || moreOpen ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500',
            )}
          >
            <MoreHorizontal className="w-6 h-6" strokeWidth={moreActive ? 2.25 : 1.75} />
            <span className="text-[10px] font-medium leading-none">More</span>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {moreOpen && (
          <div
            className="sheet fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={(e) => e.target === e.currentTarget && setMoreOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="More"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full max-w-xl rounded-t-3xl bg-gray-100 dark:bg-gray-900 px-4 pt-6"
              style={{ paddingBottom: 'var(--safe-bottom)' }}
            >
              <div className="overflow-hidden rounded-xl bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700/70">
                {MORE_TABS.map(({ path, label, icon: Icon }) => (
                  <button
                    key={path}
                    onClick={() => go(path)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-100 dark:active:bg-gray-700"
                  >
                    <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    <span className="flex-1 text-[15px] font-medium text-gray-900 dark:text-white">{label}</span>
                    {pathname === path && <span className="w-2 h-2 rounded-full bg-primary-500" />}
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                  </button>
                ))}
              </div>

              <div className="mt-4 overflow-hidden rounded-xl bg-white dark:bg-gray-800">
                <button
                  onClick={() => {
                    setMoreOpen(false)
                    window.dispatchEvent(new CustomEvent('show-net-worth-modal'))
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-gray-100 dark:active:bg-gray-700"
                >
                  <Wallet className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span className="flex-1 text-[15px] font-medium text-gray-900 dark:text-white">Net Worth</span>
                  <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                </button>
              </div>

              <button
                onClick={() => setMoreOpen(false)}
                className="mt-4 mb-4 w-full rounded-xl bg-white dark:bg-gray-800 py-3 text-[15px] font-semibold text-primary-600 dark:text-primary-400 active:bg-gray-100 dark:active:bg-gray-700"
              >
                ปิด
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  )
}

export default MobileTabBar
